/**
 * OAuth Manager
 * Centralized OAuth state management with persistence and recovery
 */

import { supabase } from '@/integrations/supabase/client';

// Storage keys
const OAUTH_ATTEMPT_KEY   = 'oauth_attempt';
const PENDING_SESSION_KEY = 'pending_oauth_session';
// FIX: Removed OAUTH_HEARTBEAT_KEY — it was defined and cleared in clearAttempt()
// but never written to. updateHeartbeat() stores the heartbeat inside the attempt
// object under OAUTH_ATTEMPT_KEY, not in a separate key. The constant was dead
// code and its removal in clearAttempt() was a no-op.

// Timing constants
export const OAUTH_TIMEOUT_MS         = 45000; // 45 seconds
export const SESSION_POLL_INTERVAL_MS = 2000;  // 2 seconds
export const HEARTBEAT_INTERVAL_MS    = 1000;  // 1 second
export const HEARTBEAT_STALE_MS       = 3000;  // 3 seconds without heartbeat = stale

export type OAuthProvider = 'google' | 'tiktok' | 'x';
export type OAuthStatus = 'idle' | 'pending' | 'success' | 'failed' | 'cancelled' | 'timeout';

export interface OAuthAttempt {
  id: string;
  provider: OAuthProvider;
  startedAt: number;
  status: OAuthStatus;
  isNative: boolean;
  lastHeartbeat?: number;
  error?: string;
}

export interface PendingSession {
  accessToken: string;
  refreshToken: string;
  provider: OAuthProvider;
  savedAt: number;
}

/**
 * Generate a unique attempt ID
 */
function generateAttemptId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Start a new OAuth attempt
 */
export function startOAuthAttempt(provider: OAuthProvider, isNative: boolean): OAuthAttempt {
  const attempt: OAuthAttempt = {
    id: generateAttemptId(),
    provider,
    startedAt: Date.now(),
    status: 'pending',
    isNative,
    lastHeartbeat: Date.now(),
  };

  try {
    localStorage.setItem(OAUTH_ATTEMPT_KEY, JSON.stringify(attempt));
    console.log(`[OAuthManager] Started ${provider} OAuth attempt:`, attempt.id);
  } catch (e) {
    console.warn('[OAuthManager] Failed to persist OAuth attempt:', e);
  }

  return attempt;
}

/**
 * Get current OAuth attempt
 */
export function getCurrentAttempt(): OAuthAttempt | null {
  try {
    const stored = localStorage.getItem(OAUTH_ATTEMPT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as OAuthAttempt;
  } catch {
    return null;
  }
}

/**
 * Update OAuth attempt status
 */
export function updateAttemptStatus(status: OAuthStatus, error?: string): void {
  try {
    const attempt = getCurrentAttempt();
    if (attempt) {
      attempt.status = status;
      if (error) attempt.error = error;
      localStorage.setItem(OAUTH_ATTEMPT_KEY, JSON.stringify(attempt));
      console.log(`[OAuthManager] Updated attempt status:`, status, error || '');
    }
  } catch (e) {
    console.warn('[OAuthManager] Failed to update attempt status:', e);
  }
}

/**
 * Clear OAuth attempt
 */
export function clearAttempt(): void {
  try {
    localStorage.removeItem(OAUTH_ATTEMPT_KEY);
    // FIX: Removed localStorage.removeItem(OAUTH_HEARTBEAT_KEY) — that key was
    // never written to, so removing it was a harmless but misleading no-op.
    console.log('[OAuthManager] Cleared OAuth attempt');
  } catch {
    // Ignore
  }
}

/**
 * Check if OAuth attempt is stale (user cancelled or browser was closed)
 */
export function isAttemptStale(): boolean {
  const attempt = getCurrentAttempt();
  if (!attempt || attempt.status !== 'pending') return false;

  const now = Date.now();

  // Check if attempt has timed out
  if (now - attempt.startedAt > OAUTH_TIMEOUT_MS) {
    console.log('[OAuthManager] Attempt timed out');
    return true;
  }

  // Check if heartbeat is stale (native only)
  if (attempt.isNative && attempt.lastHeartbeat) {
    if (now - attempt.lastHeartbeat > HEARTBEAT_STALE_MS) {
      console.log('[OAuthManager] Heartbeat is stale');
      return true;
    }
  }

  return false;
}

/**
 * Update heartbeat for native OAuth
 */
export function updateHeartbeat(): void {
  try {
    const attempt = getCurrentAttempt();
    if (attempt && attempt.status === 'pending') {
      attempt.lastHeartbeat = Date.now();
      localStorage.setItem(OAUTH_ATTEMPT_KEY, JSON.stringify(attempt));
    }
  } catch {
    // Ignore
  }
}

/**
 * Save a pending session for recovery
 */
export function savePendingSession(
  accessToken: string,
  refreshToken: string,
  provider: OAuthProvider
): void {
  try {
    const pending: PendingSession = {
      accessToken,
      refreshToken,
      provider,
      savedAt: Date.now(),
    };
    localStorage.setItem(PENDING_SESSION_KEY, JSON.stringify(pending));
    console.log(`[OAuthManager] Saved pending session for ${provider}`);
  } catch (e) {
    console.warn('[OAuthManager] Failed to save pending session:', e);
  }
}

/**
 * Get pending session
 */
export function getPendingSession(): PendingSession | null {
  try {
    const stored = localStorage.getItem(PENDING_SESSION_KEY);
    if (!stored) return null;

    const pending = JSON.parse(stored) as PendingSession;

    // Session is only valid for 5 minutes
    if (Date.now() - pending.savedAt > 5 * 60 * 1000) {
      clearPendingSession();
      return null;
    }

    return pending;
  } catch {
    return null;
  }
}

/**
 * Clear pending session
 */
export function clearPendingSession(): void {
  try {
    localStorage.removeItem(PENDING_SESSION_KEY);
    console.log('[OAuthManager] Cleared pending session');
  } catch {
    // Ignore
  }
}

/**
 * Attempt to recover a pending session
 */
export async function recoverPendingSession(): Promise<boolean> {
  const pending = getPendingSession();
  if (!pending) return false;

  console.log(`[OAuthManager] Attempting to recover ${pending.provider} session`);

  try {
    const { error } = await supabase.auth.setSession({
      access_token:  pending.accessToken,
      refresh_token: pending.refreshToken,
    });

    if (error) {
      console.error('[OAuthManager] Failed to recover session:', error);
      clearPendingSession();
      return false;
    }

    console.log('[OAuthManager] Session recovered successfully');
    clearPendingSession();
    clearAttempt();
    return true;
  } catch (e) {
    console.error('[OAuthManager] Session recovery error:', e);
    clearPendingSession();
    return false;
  }
}

/**
 * Check for existing session
 */
export async function checkSession(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  } catch {
    return false;
  }
}

/**
 * Poll for session with retry logic
 */
export async function pollForSession(
  maxAttempts: number = 15,
  intervalMs: number = SESSION_POLL_INTERVAL_MS
): Promise<boolean> {
  for (let i = 0; i < maxAttempts; i++) {
    if (await checkSession()) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  return false;
}

/**
 * Clean up all OAuth state
 */
export function cleanupOAuth(): void {
  clearAttempt();
  clearPendingSession();
}
